import { useCallback, useEffect, useState } from "react";
import {
  ghostBtnClass,
  mfgMdInputClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  getManufacturingMaterialUseDetail,
  listFinishVariantDeficits,
  reattributeFinishVariantDeficit,
} from "@/lib/manufacturing/materialUsesClient";
import type { MfgFinishVariantDeficit, MfgMaterialUseMember } from "@/lib/manufacturing/types";

/** Admin inbox: open finish-variant deficits → attribute to a color SKU. */
export function ManufacturingFinishVariantDeficitsPanel({
  enabled,
  plantId,
  refreshTick,
  filterUseId = null,
  hideWhenEmpty = false,
  onChanged,
}: {
  enabled: boolean;
  plantId: string | null;
  refreshTick: number;
  /** When set, only show deficits for this part use. */
  filterUseId?: string | null;
  /** Hide entirely when there are no matching open deficits. */
  hideWhenEmpty?: boolean;
  /** Fired after a successful resolve (so parent can refresh badges). */
  onChanged?: () => void;
}) {
  const [deficits, setDeficits] = useState<MfgFinishVariantDeficit[]>([]);
  const [membersByUse, setMembersByUse] = useState<Record<string, MfgMaterialUseMember[]>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pickByDeficit, setPickByDeficit] = useState<Record<string, string>>({});
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setBusy(true);
    setError(null);
    const r = await listFinishVariantDeficits({
      plant_id: plantId ?? undefined,
    });
    if (!r.ok) {
      setBusy(false);
      setError(r.error);
      return;
    }
    const all = r.data.deficits;
    const filtered = filterUseId ? all.filter((d) => d.use_id === filterUseId) : all;
    setDeficits(filtered);

    const useIds = [...new Set(filtered.map((d) => d.use_id))];
    const nextMembers: Record<string, MfgMaterialUseMember[]> = {};
    await Promise.all(
      useIds.map(async (useId) => {
        const detail = await getManufacturingMaterialUseDetail(useId, {
          plant_id: plantId ?? undefined,
          active_only: true,
        });
        if (detail.ok) nextMembers[useId] = detail.data.members;
      }),
    );
    setMembersByUse(nextMembers);
    setBusy(false);
  }, [enabled, plantId, filterUseId]);

  useEffect(() => {
    void reload();
  }, [reload, refreshTick]);

  async function resolve(deficit: MfgFinishVariantDeficit) {
    const material_id = pickByDeficit[deficit.id]?.trim() || "";
    if (!material_id) {
      setError("Pick a color SKU before resolving.");
      return;
    }
    setResolvingId(deficit.id);
    setError(null);
    const out = await reattributeFinishVariantDeficit({
      deficit_event_id: deficit.id,
      material_id,
    });
    setResolvingId(null);
    if (!out.ok) {
      setError(out.error);
      return;
    }
    await reload();
    onChanged?.();
  }

  if (!enabled) return null;
  if (hideWhenEmpty && !busy && deficits.length === 0 && !error) return null;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-800 dark:bg-amber-950/30">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-amber-950 dark:text-amber-100">
            Open finish-variant deficits
            {deficits.length > 0 ? ` (${deficits.length})` : ""}
          </div>
          <p className="text-xs text-amber-900/90 dark:text-amber-200/90">
            Variant bucket consumption waiting for a color SKU. Check serial/build context, then
            reattribute.
          </p>
        </div>
        <button type="button" className={ghostBtnClass} disabled={busy} onClick={() => void reload()}>
          {busy ? "Loading…" : "Reload"}
        </button>
      </div>

      {error ? <p className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</p> : null}

      {deficits.length === 0 ? (
        <p className="mt-2 text-xs text-amber-900/80 dark:text-amber-200/80">No open deficits.</p>
      ) : (
        <ul className="mt-3 max-h-64 space-y-2 overflow-y-auto">
          {deficits.map((d) => {
            const members = membersByUse[d.use_id] ?? [];
            return (
              <li
                key={d.id}
                className="rounded-md border border-amber-200/80 bg-white/70 px-2 py-2 text-xs dark:border-amber-900 dark:bg-zinc-900/40"
              >
                <div className="font-medium text-gray-900 dark:text-zinc-100">
                  {d.use_name || d.use_code} · qty {d.quantity}
                  {d.finish_channel ? (
                    <span className="ml-1 font-normal text-gray-500">({d.finish_channel})</span>
                  ) : null}
                </div>
                <div className="mt-0.5 text-gray-600 dark:text-zinc-400">
                  Serial: {d.serial_number || "—"}
                  {d.deal_id ? ` · deal ${d.deal_id.slice(0, 8)}…` : ""}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <select
                    className={mfgMdInputClass + " min-w-[12rem] text-xs"}
                    value={pickByDeficit[d.id] ?? ""}
                    onChange={(e) =>
                      setPickByDeficit((prev) => ({ ...prev, [d.id]: e.target.value }))
                    }
                  >
                    <option value="">Select color SKU…</option>
                    {members
                      .filter((m) => m.is_active)
                      .map((m) => (
                        <option key={m.id} value={m.material_id}>
                          {(m.finish_code ? `${m.finish_code} — ` : "") +
                            m.material_code +
                            " · " +
                            m.material_name}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    className={mfgPrimaryBtnClass}
                    disabled={resolvingId === d.id}
                    onClick={() => void resolve(d)}
                  >
                    {resolvingId === d.id ? "Resolving…" : "Resolve"}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
